import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import TableComponent from "../../pages/TableComponent";
import Pagination from "../../view/common/pagination/Pagination";
import { updateFilters, resetFilters } from "../../redux/slices/filterSlice";
import { RootState } from "../../redux/slices/store";
import { getData } from "../../api/ApiServices";
import "../../styles/table-search.css";
import "../../styles/ItemModels.css";
import "../../styles/modal.css";
import "../../styles/common/sub-menu.css";
import "../../styles/common/spinner.css";
import "../../styles/common/toplayout.css";
import "../../styles/common/btn.css";
import "../../styles/common/icon-style.css";
import "../../styles/common/table-heading-icons.css";
import { GrnData } from "../../models/GRnData";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const customStyles = `
  .grn-report-header {
    margin-bottom: 20px;
  }
  .grn-report-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 10px;
  }
  .date-filters-container {
    margin-bottom: 20px;
    padding: 15px;
    background-color: #f9f9f9;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
  }
  .date-filters {
    display: flex;
    gap: 20px;
    align-items: center;
    flex-wrap: wrap;
  }
  .date-filter-group {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .date-filter-label {
    font-weight: 500;
  }
  .date-input {
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;
  }
  .filter-button {
    padding: 8px 16px;
    background-color:#ff7a00;;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
  }
  .filter-button:hover {
    // background-color: #e5e5e5;
  }
  .clear-filter-button {
    padding: 8px 16px;
    background-color: #f5f5f5;
    color: #333;
    border: 1px solid #ccc;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
  }
  .clear-filter-button:hover {
    background-color: #e5e5e5;
  }
  .table-container {
    margin-top: 20px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 15px;
    background-color: #fff;
  }
  .error-message {
    color: #d32f2f;
    padding: 10px;
    background-color: #ffebee;
    border-radius: 4px;
    margin-bottom: 15px;
  }
  .validation-error {
    color: #d32f2f;
    font-size: 14px;
    margin-top: 5px;
  }
  .active-filter-message {
    margin-top: 10px;
    padding: 8px 12px;
    background-color: #e8f4fd;
    border-left: 4px solid #2196f3;
    color: #0d47a1;
    font-size: 14px;
    border-radius: 0 4px 4px 0;
  }
  .error-filter-message {
    margin-top: 10px;
    padding: 8px 12px;
    background-color:rgb(252, 239, 235);
    border-left: 4px solid rgb(255, 0, 0);
    color:rgb(255, 0, 0);
    font-size: 14px;
    border-radius: 0 4px 4px 0;
  }
  .no-records-message {
    padding: 20px;
    text-align: center;
    font-size: 15px;
    color: #666;
    background-color: #f9f9f9;
    border-radius: 4px;
    margin: 20px 0;
  }
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
  }
  .table-wrapper {
    position: relative;
  }
`;

const GrnReport: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const filters = useSelector((state: RootState) => state.filters);

  const [grnData, setGrnData] = useState<GrnData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("grnDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [dateValidationError, setDateValidationError] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<boolean>(false);

  const [appliedStartDate, setAppliedStartDate] = useState<Date | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | null>(null);
  const [initializeGRNReport, setGRNReportInitialized] = useState(false);


  const today = new Date().toISOString().split("T")[0];
  const [startdate, setStartdate] = useState<string>(today);
  const [enddate, setEnddate] = useState<string>(today);

  useEffect(() => {
    dispatch(resetFilters());
    setGRNReportInitialized(true);
  }, [dispatch]);

  const columns = [
    { key: "grnNo", label: "GRN No", sortable: true },
    { key: "grnDate", label: "GRN Date", sortable: true },
    {
      key: "supplierId",
      label: "supplier Code",
      nameKey: "code",
      render: (value: any) =>
        value && typeof value === "object" ? value.code || "N/A" : "N/A",
    },
    {
      key: "partId",
      label: "PartNo",
      nameKey: "part.internalPartNo",
      render: (value: any) => {
        if (
          value &&
          typeof value === "object" &&
          value.part &&
          value.part.internalPartNo
        ) {
          return value.part.internalPartNo;
        }
        return "N/A";
      },
    },
    {
      key: "partId",
      label: "PartName",
      nameKey: "part.partName",
      render: (value: any) => {
        if (
          value &&
          typeof value === "object" &&
          value.part &&
          value.part.partName
        ) {
          return value.part.partName;
        }
        return "N/A";
      },
    },
    { key: "number", label: "Invoice", sortable: true },
    { key: "qty", label: "Quantity", isNumeric: true, sortable: true },
  ];

  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "-");
  };

  const dateFormatDDMMYYYY = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  const [grnStartDate, setGrnStartdate] = useState<Date | null>(new Date());
  const [grnEndDate, setGrnEndDate] = useState<Date | null>(new Date());

  const applyDateFilter = () => {
    if (grnStartDate == null) {
      setDateValidationError("Start date is required.");
      setActiveFilter(false);
      return;
    }

    if (grnEndDate == null) {
      setDateValidationError("End date is required.");
      setActiveFilter(false);
      return;
    }
    if (grnStartDate && grnEndDate && grnStartDate > grnEndDate) {
      setDateValidationError("Start date cannot be after end date.");
      setActiveFilter(false);
      return;
    }

    setDateValidationError("");

    setAppliedStartDate(grnStartDate);
    setAppliedEndDate(grnEndDate);

    setActiveFilter(true);

    setCurrentPage(1);

    fetchGrnData();
  };

  const clearDateFilter = () => {
    setStartdate("");
    setEnddate("");

    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setDateValidationError("");
    setActiveFilter(false);
    setCurrentPage(1);
  };

  const fetchGrnData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const queryParams: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        filter: filters,
      };

      if (grnStartDate != null) {
        queryParams.startdate = grnStartDate.toISOString().split("T")[0];
      }
      if (grnEndDate != null) {
        queryParams.enddate = grnEndDate.toISOString().split("T")[0];
      }

      const response: any = await getData<{
        data: GrnData[];
        total: number;
        pages: number;
      }>("/grns", queryParams);
      console.log("API Response:", response);

      const formattedData = response.data.data.map((item: any) => {
        let enhancedItem = {
          ...item,
          grnDate: dateFormatDDMMYYYY(item.grnDate),
        };

        if (item.partId && item.partId.part) {
          enhancedItem.partDetails = JSON.stringify({
            internalPartNo: item.partId.part.internalPartNo,
            externalPartNo: item.partId.part.externalPartNo,
            partName: item.partId.part.partName,
            hsnCode: item.partId.part.hsnCode,
            packingQty: item.partId.part.packingQty,
            status: item.partId.part.status,
          });
        }

        if (item.poId) {
          enhancedItem.poDetails = JSON.stringify({
            poNo: item.poId.poNo || "N/A",
            poDate: item.poId.poDate
              ? new Date(item.poId.poDate).toLocaleDateString("en-CA")
              : "N/A",
          });
        }

        if (item.poId && item.poId.poNo) {
          enhancedItem.poNo = item.poId.poNo;
        }

        return enhancedItem;
      });

      if (response && response.data) {
        setGrnData(formattedData);
        setTotalItems(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      } else {
        setGrnData([]);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (err) {
      console.error("Error fetching GRN data:", err);
      setError("Failed to load GRN report data. Please try again later.");
      setGrnData([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderValue: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderValue);
  };


  const handleViewGrn = (row: GrnData) => {
    console.log("View GRN:", row);
  };


  const handleResetFilters = () => {
    dispatch(resetFilters());
    setStartdate("");
    setEnddate("");
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setDateValidationError("");
    setActiveFilter(false);
  };

  useEffect(() => {
    if (initializeGRNReport) {
      fetchGrnData();
    }
  }, [initializeGRNReport, currentPage, itemsPerPage, sortBy, sortOrder, filters]);

  if (isLoading && grnData.length === 0) {
    return (
      <div className="item-models-container">
        <style>{customStyles}</style>
        <div className="spinner"></div>
      </div>
    );
  }

  function onChangeHandlerGrnStartDate(value: Date | null) {
    setGrnStartdate(value);
  }

  function onChangeHandlerGrnEndDate(value: Date | null) {
    setGrnEndDate(value);
  }

  const CustomInput = React.forwardRef(({ value, onClick }: any, ref: any) => (
    <input
      readOnly
      className="date-input"
      onClick={onClick}
      value={value}
      ref={ref}
      style={{ width: "130px" }}
    />
  ));

  return (
    <div className="item-models-container">
      
      <style>{customStyles}</style>

      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Inventory</span>
              <span>GRN Report</span>
            </div>
          </div>
        </div>
      </div>

      <div>
       
        <div className="date-filters-container">
          <div className="date-filters">
            <div className="date-filter-label">Filtering By GRN Dates</div>
            <div className="date-filter-group">
              <label className="date-filter-label" htmlFor="start-date">
                Start Date:
              </label>
          
              {/* <input
                id="start-date"
                className="date-input"
                type="date"
                value={startdate}
                onChange={(e) => setStartdate(e.target.value)}
                max={enddate || today}
              /> */}
              <DatePicker
                selected={grnStartDate}
                onChange={onChangeHandlerGrnStartDate}
                dateFormat="dd '- 'MM '- 'yyyy"
                placeholderText="Select Start Date"
                customInput={<CustomInput />}
                maxDate={new Date() ?? undefined}
              />
            </div>

            <div className="date-filter-group">
              <label className="date-filter-label" htmlFor="end-date">
                End Date:
              </label>
             
              {/* <input
                id="end-date"
                className="date-input"
                type="date"
                value={enddate}
                onChange={(e) => setEnddate(e.target.value)}
                min={startdate}
                max={today}
              /> */}
              <DatePicker
                selected={grnEndDate}
                onChange={onChangeHandlerGrnEndDate}
                dateFormat="dd '- 'MM '- 'yyyy"
                placeholderText="Select End Date"
                customInput={<CustomInput />}
                minDate={grnStartDate ?? undefined}
                maxDate={new Date()  ?? undefined}
              />
            </div>

            <button className="filter-button" onClick={applyDateFilter}>
              Apply Filter
            </button>

   
            {/* <button className="clear-filter-button" onClick={clearDateFilter}>
              Clear Dates
            </button> */}
          </div>

          {dateValidationError && (
            <div className="error-filter-message">{dateValidationError}</div>
          )}

          {activeFilter && (
            <p className="active-filter-message">
              {appliedStartDate && appliedEndDate
                ? `Showing data from ${dateFormatDDMMYYYY(
                    appliedStartDate.toISOString().split("T")[0]
                  )} to ${dateFormatDDMMYYYY(
                    appliedEndDate.toISOString().split("T")[0]
                  )}`
                : // Restored commented alternative formatting
                  // : appliedStartDate
                  // ? `Showing data from ${formatDisplayDate(appliedStartDate)}`
                  // : appliedEndDate
                  // ? `Showing data until ${formatDisplayDate(appliedEndDate)}`
                  ""}
            </p>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="box-container">
          <div className="table-wrapper">
            <TableComponent
              columns={columns}
              data={grnData}
              setFilter={(filters: Record<string, any>) => {
                Object.entries(filters).forEach(([key, value]) => {
                  dispatch(updateFilters({ key, value: String(value) }));
                });
              }}
              setSorting={handleSortChange}
              defaultSortConfig={{ key: sortBy, direction: sortOrder }}
              excludeFields={[]} 
              context="default" 
            />
            {isLoading && (
              <div className="loading-overlay">
                <div className="spinner"></div>
              </div>
            )}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalItems={totalItems}
          />
        </div>
      </div>
    </div>
  );
};

export default GrnReport;